import { useAppSelector } from "app/hooks";
import { getTenantSettings } from "reduxState/settings/settings";
import { Nullable } from "types/global";
import {
  CommunicationSettingsModel,
  GeneralSettingsModel,
  RewardsSettingsModel,
  SettingsModel,
  TiersSettingsModel,
} from "types/settings";

export function useBasicSettingsData(mode: "communication"): {
  formValues: Nullable<CommunicationSettingsModel>;
  otherValues: Nullable<Omit<SettingsModel, keyof CommunicationSettingsModel>>;
};
export function useBasicSettingsData(mode: "general"): {
  formValues: Nullable<GeneralSettingsModel>;
  otherValues: Nullable<Omit<SettingsModel, keyof GeneralSettingsModel>>;
};
export function useBasicSettingsData(mode: "rewards"): {
  formValues: Nullable<RewardsSettingsModel>;
  otherValues: Nullable<Omit<SettingsModel, keyof RewardsSettingsModel>>;
};
export function useBasicSettingsData(mode: "tiers"): {
  formValues: Nullable<TiersSettingsModel>;
  otherValues: Nullable<Omit<SettingsModel, keyof TiersSettingsModel>>;
};
export function useBasicSettingsData(
  mode: "communication" | "general" | "tiers" | "rewards",
) {
  const settings = useAppSelector(getTenantSettings);

  if (settings) {
    const data: SettingsModel = settings;
    switch (mode) {
      case "communication": {
        const {
          expirePointsNotificationDays,
          expireCouponsNotificationDays,
          expireLevelsNotificationDays,
          ...rest
        } = data;
        return {
          formValues: {
            expirePointsNotificationDays,
            expireCouponsNotificationDays,
            expireLevelsNotificationDays,
          },
          otherValues: { ...rest },
        };
      }
      case "general": {
        const {
          accountActivationRequired,
          activeMember,
          customEventCondition,
          transactionCondition,
          allowCustomersProfileEdits,
          excludeDeliveryCostsFromTierAssignment,
          excludedDeliverySKUs,
          maxPointsRedeemed,
          identificationMethod,
          programName,
          programUrl,
          timezone,
          ...rest
        } = data;
        return {
          formValues: {
            accountActivationRequired,
            activeMember,
            customEventCondition,
            transactionCondition,
            allowCustomersProfileEdits,
            excludeDeliveryCostsFromTierAssignment,
            excludedDeliverySKUs,
            maxPointsRedeemed,
            identificationMethod,
            programName,
            programUrl,
            timezone,
          },
          otherValues: { ...rest },
        };
      }
      case "tiers": {
        const {
          excludedLevelSKUs,
          levelDowngradeBase,
          levelDowngradeDays,
          levelDowngradeMode,
          levelResetPointsOnDowngrade,
          tierAssignType,
          tierWalletCode,
          tiersMode,
          ...rest
        } = data;
        return {
          formValues: {
            excludedLevelSKUs,
            levelDowngradeBase,
            levelDowngradeDays,
            levelDowngradeMode,
            levelResetPointsOnDowngrade,
            tierAssignType,
            tierWalletCode,
            tiersMode,
          },
          otherValues: { ...rest },
        };
      }
      case "rewards": {
        const { rewardWalletCode, ...rest } = data;
        return {
          formValues: { rewardWalletCode },
          otherValues: { ...rest },
        };
      }
    }
  } else {
    return {
      formValues: null,
      otherValues: null,
    };
  }
}
